import { ADDRESS_TYPE } from 'containers/customers/constants';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { Attachment } from 'containers/receipts/receipt.model';
import { API_RESPONSES, AttachmentFile } from 'types/baseTypes';
import { LineItem } from 'containers/invoices/invoices.model';
import { TeamIdPayload } from 'containers/home/home.model';
import {
  CheckoutSessionStatus,
  ESTIMATE_ATTRIBUTE_TYPE,
  SubscriptionDescription,
  SubscriptionFrequency,
  SubscriptionPlanName,
  SubscriptionPlanType,
  SubscriptionProcessingStatus
} from './constants';
import { LeadSource } from 'containers/jobs/constant';
import { TermsSuggestion } from 'containers/settings/settings.model';
import { ReminderCondition } from 'containers/settings/estimate-settings/estimateSetting.model';

export interface Estimate {
  id: string;
  teamId: number;
  title: string;
  description: string;
  estimateNumber: string;
  customerId: string;
  customerName: string;
  merchantId: string;
  merchantName: string;
  merchantProfilePicture: string;
  status: string;
  sentAt: string;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  expiresAt: string;
  note: string;
  referenceNo: string;
  hasDiscount: boolean;
  discountType: string;
  discount: string;
  surchargeAmount: string;
  serviceChargeAmount: string;
  amount: string;
  isServiceFeeEnabled: boolean;
  lineItems: LineItem[];
  taxes: EstimateTax[];
  serviceCharges: EstimateServiceCharge[];
  customer: EstimateCustomer;
  attachments: Attachment[];
  timeline: EstimateTimelineEvent[];
  publicUrl: string;
  messageToCustomer: string;
  isSignatureEnabled: boolean;
  signature: {
    customerName: string;
    url: string;
    signedAt: string;
  };
  team: {
    name: string;
    profilePicture: string;
  };
  billableAmount: string;
  payableAmount: string;
  tnc: string;
  tncEdited: boolean;
  assignees: AssignableMember[];
  jobs: RelatedJob[];
  invoices: InvoiceDetails[];
  hasSigned: boolean;
  customerComment: string;
  leadSource: string;
  leadSourceType: LeadSource;
  reminderEnabled: boolean;
  reminders: EstimateReminder[];
  hasExpiry: boolean;
}

export interface CreateEstimatePayload {
  title: string;
  description: string;
  customerId: string;
  customerServiceAddressId: number;
  expiresAt: string;
  referenceNo: string;
  discount: number;
  discountType: 'FLAT' | 'PERCENTAGE';
  assignees: string[];
  isServiceFeeEnabled: boolean;
  surchargeAmount: number;
  serviceChargeAmount: number;
  lineItems: LineItemForCreateEstimate[];
  attachments: AttachmentForCreateEstimate[];
  notes: Notes;
  taxes: Tax[];
  serviceCharges: ServiceCharge[];
  isSignatureEnabled: boolean;
  tnc: string;
  relativeEntity?: {
    entityType: 'JOB' | 'INVOICE';
    entityId: string;
    entityNumber: string;
  };
  leadSource: string;
  leadSourceType: LeadSource;
  source: string;
  reminderEnabled: boolean;
  reminders: EstimateReminder[];
}

export interface EstimatePDFPreviewPayload {
  teamId: number;
  payload: CreateEstimatePayload & { estimateNumber: string };
}

export interface GenericApiResponse<T> {
  data: {
    message: API_RESPONSES;
    data?: T;
  };
}

export interface CreateEstimateResponse {
  estimateId: string;
  estimateNumber: string;
}

export type UpdateEstimateDescriptionResponse = GenericApiResponse<void>;

export interface EstimateResponse {
  data: Estimate;
}

export interface LineItemForCreateEstimate {
  catalogueLineItemId: number;
  name: string;
  qty: number;
  amount: number;
  unitPrice: number;
  sku: string;
  additionalDetails: string;
  taxes: Tax[];
}

export interface AttachmentForCreateEstimate {
  title: string;
  url: string;
  description: string;
  source: string;
  id?: string;
}

export interface Notes {
  comment: string;
}

export interface Tax {
  name: string;
  percentage: number;
  amount: number;
  catalogueTaxId?: number;
  additionalTaxId?: number;
}

export interface ServiceCharge {
  name: string;
  amount: number;
  percentage: number;
  totalAmount: number;
  taxes: Tax[];
}

export interface EstimateByIdResponse {
  data: Estimate;
}

export interface EstimateLineItem {
  id: number;
  estimateId: string;
  name: string;
  qty: string;
  amount: string;
  unitPrice: string;
  additionalDetails: string;
  sku: string;
  catalogueLineItemId: number;
  createdAt: string;
  updatedAt: string;
}

export interface EstimateTax {
  id: number;
  estimateId: string;
  name: string;
  percentage: string;
  amount: string;
  catalogueTaxId: number | null;
  additionalTaxId: number | null;
}

export interface EstimateServiceCharge {
  id: number;
  estimateId: string;
  name: string;
  amount: string;
  percentage: string;
  totalAmount: string;
  createdAt: string;
  updatedAt: string;
  taxes: EstimateServiceChargeTax[];
}

export interface EstimateServiceChargeTax {
  id: number;
  estimateServiceChargeId: number;
  name: string;
  percentage: string;
  amount: string;
  catalogueTaxId: number | null;
}

export interface EstimateCustomer {
  id: string;
  customerNumber: string;
  name: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  referenceNumber: string;
  note: string;
  billingAddressId: number;
  isBillingSameAsService: boolean;
  source: string;
  serviceAddress: EstimateCustomerAddress[];
  billingAddress: EstimateCustomerAddress;
}

export interface EstimateCustomerAddress {
  id: number;
  estimateId: string;
  customerId: string;
  addressId: number;
  type: ADDRESS_TYPE;
  street: string;
  line2: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  addressNote: string;
  addressName: string;
  source: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface EstimateTimelineEvent {
  title: string;
  subTitle: string;
  subText: string;
  noteId: number;
  attachments: any[] | null;
  timestamp: string;
  merchantId: string;
  type: string;
}

export enum EstimateTimelineEventType {
  Action = 'ACTION',
  Note = 'NOTE',
  EstimateNote = 'ESTIMATE_NOTE'
}

export interface EstimateByIdApiResponse {
  data: EstimateByIdResponse;
}

export interface EstimateListItem {
  id: string;
  title: string;
  estimateNumber: string;
  customerId: string;
  customerName: string;
  merchantId: string;
  merchantName: string;
  merchantProfilePicture: string;
  amount: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  relatedEntities: {
    job?: {
      entityId: string;
      entityNumber: string;
    };
    invoice?: {
      entityId: string;
      entityNumber: string;
    };
  };
  assignees: AssignableMember[];
  updatedAt: string;
}

export interface Metadata {
  count: number;
}

export interface Summary {
  invoicedEstimatesAmount: string;
  pendingEstimatesAmount: string;
}

export interface Metadata {
  count: number;
}

export interface Summary {
  invoicedEstimatesAmount: string;
  pendingEstimatesAmount: string;
}

export interface GetEstimatesResponse {
  data: {
    estimates: EstimateListItem[];
    metadata: Metadata;
    summary: Summary;
  };
}

export interface GetEstimatesParams {
  teamId: number;
  fromCreatedAt?: string;
  toCreatedAt?: string;
  fromExpiresAt?: string;
  toExpiresAt?: string;
  status?: string | string[];
  customerIds?: string | string[];
  memberIds?: string | string[];
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetEstimateTimelinePayload {
  estimateId: string;
  teamId: number;
  timezone: string;
}

export interface GetEstimateTimelineResponse {
  data: {
    timelineList: EstimateTimelineEvent[];
  };
}

export interface GenericResponse<T = void> {
  data: T;
  message: string;
  success: boolean;
}

export interface CreateEstimateNotePayload {
  teamId: number;
  estimateId: string;
  attachments?: AttachmentFile[];
  comment: string;
}

export type CreateEstimateNoteResponse = GenericResponse<{
  noteId: number;
}>;

export interface EditEstimateNotePayload {
  teamId: number;
  estimateId: string;
  noteId: number;
  attachments?: AttachmentFile[];
  comment: string;
}

export type EditEstimateNoteResponse = GenericResponse;

export interface DeleteEstimateNotePayload {
  estimateId: string;
  teamId: number;
  noteId: number;
}

export type DeleteEstimateNoteResponse = GenericResponse;

export interface RelatedJob {
  jobId: string;
  jobNumber: string;
  jobTitle: string;
  jobStatus: string;
  createdBy: string;
  profilePicture: string;
  createdAt: string;
  isScheduled: boolean;
  scheduledFrom: string;
  scheduledTo: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: {
      id: number;
      type: string;
      street: string;
      line2: string;
      apartment: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      addressNote: string;
    };
  };
  payableAmount: string;
  merchantName: string;
  hasPermission: boolean;
  isEditable?: boolean;
}

export interface InvoiceDetails {
  id: string;
  invoiceNumber: string;
  status: string;
  amountPayable: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    profilePicture: string;
  };
  hasPermission: boolean;
}

export interface ShareEstimatePayload {
  teamId: number;
  estimateId: string;
  phoneNumber?: string;
  email?: string;
  type: 'sms' | 'email';
  updateCustomer: boolean;
}

export interface EstimateReferenceCheckResponse {
  data: {
    isExists: boolean;
    estimateNumber: string[];
  };
}

export interface CheckEstimateAttribute extends TeamIdPayload {
  body: { attribute: ESTIMATE_ATTRIBUTE_TYPE; value: string };
}

export interface UpdateEstimateLeadSourcePayload {
  leadSourceType: string;
  leadSource: string;
  teamId: number;
  estimateId: string;
}

export interface UpdateEstimateLeadSourceResponse {
  data: {
    estimate_id: string;
  };
}

export interface GetTncSuggestionsResponse {
  data: TermsSuggestion[];
}

export interface EstimateSummary {
  APPROVED: number;
  ARCHIVED: number;
  EXPIRED: number;
  PENDING: number;
  REJECTED: number;
}

export interface GetEstimateSummaryResponse {
  data: EstimateSummary;
}

export interface UpdateEstimateTncPayload {
  teamId: number;
  body: {
    tnc: string;
    applyPendingEstimates: boolean;
  };
}

export interface UpdateEstimateTncResponse {
  data: {
    success: boolean;
    message: string;
  };
}

export interface TeamLevelEstimateTncSettings {
  tnc: string;
  enabled: boolean;
}

export interface UpdateEstimateTncStatusPayload {
  teamId: number;
  enabled: boolean;
}

export interface CheckoutSessionResponse {
  data: {
    session_id: string;
    client_secret: string;
  };
}

interface AutomaticTax {
  enabled: boolean;
  liability: null;
  status: string;
}

interface CustomText {
  after_submit: null;
  shipping_address: null;
  submit: null;
  terms_of_service_acceptance: null;
}

interface PaymentMethodConfigurationDetails {
  id: string;
  parent: string;
}

interface CardOptions {
  installments: null;
  request_three_d_secure: string;
  setup_future_usage: string;
  statement_descriptor_suffix_kana: string;
  statement_descriptor_suffix_kanji: string;
}

interface PaymentMethodOptions {
  card: CardOptions;
  acss_debit: null;
  affirm: null;
  afterpay_clearpay: null;
  alipay: null;
  amazon_pay: null;
  au_becs_debit: null;
  bacs_debit: null;
  bancontact: null;
  boleto: null;
  cashapp: null;
  customer_balance: null;
  eps: null;
  fpx: null;
  giropay: null;
  grabpay: null;
  ideal: null;
  klarna: null;
  konbini: null;
  link: null;
  mobilepay: null;
  multibanco: null;
  oxxo: null;
  p24: null;
  paynow: null;
  paypal: null;
  pix: null;
  revolut_pay: null;
  sepa_debit: null;
  sofort: null;
  swish: null;
  us_bank_account: null;
}

interface PhoneNumberCollection {
  enabled: boolean;
}

interface SavedPaymentMethodOptions {
  allow_redisplay_filters: string[];
  payment_method_remove: string;
  payment_method_save: string;
}

interface TotalDetails {
  amount_discount: number;
  amount_shipping: number;
  amount_tax: number;
  breakdown: null;
}

export interface CheckoutSessionDetails {
  data: {
    after_expiration: null;
    allow_promotion_codes: boolean;
    amount_subtotal: number;
    amount_total: number;
    automatic_tax: AutomaticTax;
    billing_address_collection: string;
    cancel_url: string;
    client_reference_id: string;
    client_secret: string;
    consent: null;
    consent_collection: null;
    created: number;
    currency: string;
    currency_conversion: null;
    customer: null;
    customer_creation: string;
    customer_details: null;
    customer_email: string;
    custom_fields: any[];
    custom_text: CustomText;
    expires_at: number;
    id: string;
    invoice: null;
    invoice_creation: null;
    line_items: null;
    livemode: boolean;
    locale: string;
    metadata: Record<string, any>;
    mode: string;
    object: string;
    payment_intent: null;
    payment_link: null;
    payment_method_collection: string;
    payment_method_configuration_details: PaymentMethodConfigurationDetails;
    payment_method_options: PaymentMethodOptions;
    payment_method_types: string[];
    payment_status: string;
    phone_number_collection: PhoneNumberCollection;
    recovered_from: string;
    redirect_on_completion: string;
    return_url: string;
    saved_payment_method_options: SavedPaymentMethodOptions;
    setup_intent: null;
    shipping_address_collection: null;
    shipping_cost: null;
    shipping_details: null;
    shipping_options: any[];
    status: CheckoutSessionStatus;
    submit_type: string;
    subscription: null;
    success_url: string;
    tax_id_collection: null;
    total_details: TotalDetails;
    ui_mode: string;
    url: string;
  };
}

export interface InitiateSubscriptionPayload {
  subscriptionPlanId: number;
  subscriptionPlanName: string;
  token?: string;
  type: 'WEB';
  status: SubscriptionProcessingStatus;
  additionalData?: any;
}

export interface InitiateSubscriptionResponse {
  data: {
    success: boolean;
    message: string;
  };
}

export interface CompleteSubscriptionResponse {
  data: {
    success: boolean;
    message: string;
  };
}

export interface SubscriptionPlan {
  id: number;
  name: SubscriptionPlanName;
  description: SubscriptionDescription;
  type: SubscriptionPlanType;
  code: string;
  price: number;
  duration: number;
  frequency: SubscriptionFrequency;
  isActive: boolean;
  hasFreeTrial: boolean;
  trialDuration: number;
}

export interface GetSubscriptionPlansResponse {
  data: SubscriptionPlan[];
}

export interface CancelSubscriptionResponse {
  data: {
    success: boolean;
    message: string;
  };
}

export interface RenewSubscriptionResponse {
  data: {
    createCheckoutSession: boolean;
  };
}

export interface CreateCheckoutSessionRequest {
  includeFreeTrial: boolean;
  subscriptionPlanName: string;
}

export interface EstimateLevelAttachments {
  attachments: AttachmentFile[];
  createdAt: string;
  createdBy: string;
}

export interface EstimateNoteAttachment {
  attachments: AttachmentFile[];
  comment: string;
  createdAt: string;
  createdBy: string;
}

export interface GetEstimateAttachmentsResponse {
  estimateLevelAttachments: EstimateLevelAttachments;
  estimateNoteLevelAttachments: EstimateNoteAttachment[];
}

// Create/Edit estimate flow reminder type
export interface EstimateReminder {
  id: string;
  name: string;
  condition: ReminderCondition;
  remindOn: string;
  isSent: boolean;
}

export interface UpdateEstimateDetailRemindersPayload {
  teamId: number;
  estimateId: string;
  reminders: EstimateReminder[];
}

export interface UpdateEstimateDetailRemindersResponse {
  data: {
    success: boolean;
    message: string;
  };
}
